import React, {useState} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {Formik} from 'formik';
import isIsraeliIdValid from 'israeli-id-validator';
import Step2View from './Step2-view';
import {loadOptionsByDDType} from '../../actions/loadOptionsByDDType';
import {checkRequiredValues} from '../../utils/forms/checkRequiredValues';
import types from '../../types';
import {errors as E} from '../../constants/errors';
import {fileTypesIds} from '../../constants/fileTypeIds';
import {ddTypes} from '../../constants/dropdownTypes';
import {regTypes} from '../../constants/regTypes';
import {checkNextStepAndGo} from '../../utils/checkNextStepAndGo';
import {
  convertISOStringToDateWithTimezoneOffset,
  convertDateWithTimezoneOffsetToEpoch
} from '../../utils/forms/normalizeTime';
import {useIntl} from "react-intl";
import {trackEvent} from "../../utils/trackEvent";

const Step2 = ({
                 submitStep,
                 uploadFile,
                 uploadIdFile,
                 regType,
                 payload,
                 language,
                 showGlobalErrorMessage,
                 prevCompletedStepSign,
                 prevCompletedStepSignFalse,
                 checkIdNumber
               }) => {
  const {
    gender,
    citizenship,
    country,
    kupatHolim,
    familyStatus,
    birthDate,
    dateOfAliyah,
    idNumber,
    idFileUploaded,
    cameFrom,
    ...restPayload
  } = payload;
  const [idNumberUsed, setIdNumberUsed] = useState(false);
  const initialValues = {
    firstName: '',
    lastName: '',
    gender: gender ? {value: gender} : '',
    citizenship: citizenship ? {value: citizenship} : '',
    liveOutsideIsrael: false,
    idNumber: idNumber || '',
    uploadId: null,
    country: country ? {value: country} : '',
    passport: idNumber || '',
    uploadPassport: null,
    uploadPermit: null,
    birthDate: (birthDate) ? convertISOStringToDateWithTimezoneOffset(birthDate) : null,
    kupatHolim: kupatHolim ? {value: kupatHolim} : '',
    familyStatus: familyStatus ? {value: familyStatus} : '',
    partnerFirstName: '',
    partnerLastName: '',
    dateOfAliyah: (dateOfAliyah) ? convertISOStringToDateWithTimezoneOffset(dateOfAliyah) : null,
    idFileUploaded,
    cameFrom: cameFrom ? {value: cameFrom} : '',
    ...restPayload
  };

  const validate = values => {
    const {
      firstName,
      lastName,
      gender,
      citizenship,
      idNumber,
      uploadId,
      country,
      passport,
      uploadPassport,
      uploadPermit,
      birthDate,
      kupatHolim,
      familyStatus,
      partnerFirstName,
      partnerLastName,
      cameFrom,
      cameFromText
    } = values;

    const requiredForAll = {
      firstName,
      lastName,
      gender,
      citizenship,
      birthDate,
      familyStatus,
    };
    const requiredForIsraeliOnly = {
      idNumber,
      kupatHolim,
    };
    const requiredFilesForIsraeliOnly = (idFileUploaded) ? {} : {
      uploadId,
    };
    const requiredForForeignOnly = {
      country,
      passport,
    };
    const requiredFilesForForeignOnly = (idFileUploaded) ? {} : {
      uploadPassport,
      uploadPermit,
    };
    const requiredForMarriedOnly = {
      partnerFirstName,
      partnerLastName,
    };
    let errors = {};

    errors = checkRequiredValues(requiredForAll, errors);

    if (values.citizenship.value === 2) {
      errors = checkRequiredValues({
        ...requiredForForeignOnly,
        ...requiredFilesForForeignOnly
      }, errors);
    } else {
      errors = checkRequiredValues({
        ...requiredForIsraeliOnly,
        ...requiredFilesForIsraeliOnly
      }, errors);
      if (!isIsraeliIdValid(values.idNumber)) {
        errors.idNumber = E.INVALID_ID
      }
    }

    if (values.familyStatus.value === 2) {
      errors = checkRequiredValues(requiredForMarriedOnly, errors);
    }

    if (regType === regTypes.ALL) {
      errors = checkRequiredValues({cameFrom}, errors);

      if (values.cameFrom.value === 6) {
        errors = checkRequiredValues({cameFromText}, errors);
      }
    }

    return errors;
  };

  const {formatMessage} = useIntl();

  const handleSubmit = values => {
    Promise.resolve()
      .then(() => {
        if (!idFileUploaded) return Promise.all(submitFiles(values));
      })
      .then((result) => {
        const allVerificationsPassed = result && result.filter(Boolean).length > 0
          ? result[0]?.overallVerification?.allVerificationsPassed : true;

        const {citizenship} = values;
        if (citizenship.value === 2) {
          handleCommonSubmit(values);
        } else {
          if (!allVerificationsPassed) {
            handleVerificationErrors(result[0])
          } else {
            handleCommonSubmit(values);
          }
        }
      })
      .then(() => trackEvent('signup_step',
          {
            step_number: 2,
            step_name_en: 'Personal details',
            step_name_he: formatMessage({id: 'step2.page.title', defaultMessage: ''}),
            regType
          }
        )
      )
  };

  const handleVerificationErrors = (data) => {
    const validName = data?.overallVerification?.nameStatus;
    const validStatus = data?.overallVerification?.idStatus;
    const validImage = data?.image_analysis?.validId;

    const idOrNameStatusError = validName === 'FAIL' || validStatus === 'FAIL'
      ? E.STEP_2_ID_IMAGE_NAME_OR_ID_ERROR : '';
    const imageError = !validImage ? E.STEP_2_ID_IMAGE_GENERAL_ERROR : '';
    const errorMessage = [
      idOrNameStatusError,
      <div>{'\n'}</div>,
      imageError,
    ].filter(Boolean);

    showGlobalErrorMessage(errorMessage);
  };

  const handleCommonSubmit = values => {
    Promise.resolve()
      .then(() => submitFields(values))
      .then(checkNextStepAndGo)
      .then(() => prevCompletedStepSignFalse())
      .catch(err => console.error({err}));
  };

  const submitFiles = values => {
    const {
      uploadPassport,
      uploadPermit,
      uploadId,
      firstName,
      lastName,
      idNumber,
      citizenship
    } = values;
    return (citizenship.value === 2)
      ? sendFilesForForeign({uploadPassport, uploadPermit})
      : sendFilesForIsraeli({uploadId}, firstName, lastName, idNumber, 'IL');
  };

  const sendFilesForForeign = ({uploadPassport, uploadPermit}) => {
    return [
      uploadFile({
        file: uploadPassport[0],
        fileTypeId: fileTypesIds.step2.passport
      }),
      uploadFile({
        file: uploadPermit[0],
        fileTypeId: fileTypesIds.step2.workingVisaPermit
      }),
    ];
  };

  const sendFilesForIsraeli = ({uploadId}, firstName, lastName, idNumber, nationality) => {
    return [
      uploadIdFile({
        file: uploadId[0],
        fileTypeId: fileTypesIds.step2.israeliId,
        firstName,
        lastName,
        idNumber,
        nationality
      })
    ];
  };

  const submitFields = values => {
    const data = (values.citizenship.value === 2)
      ? getDataForForeign(values)
      : getDataForIsraeli(values);
    return submitStep({
      step: 2,
      data: {prevCompletedStepSign, ...data},
    })
  };

  const getDataForIsraeli = values => {
    const {
      idNumber,
      liveOutsideIsrael,
      kupatHolim
    } = values;
    return {
      ...getCommonData(values),
      idNumber,
      liveOutsideIsrael,
      kupatHolim: kupatHolim.value,
    }
  };

  const getDataForForeign = values => {
    const {passport, country} = values;
    return {
      ...getCommonData(values),
      idNumber: passport,
      country: country.value,
      kupatHolim: 5 // foreign users are not a kupat holim members anyway
    }
  };

  const getCommonData = values => {
    const {
      firstName,
      lastName,
      gender,
      citizenship,
      birthDate,
      familyStatus,
      partnerFirstName,
      partnerLastName,
      dateOfAliyah,
      cameFrom, cameFromText
    } = values;

    return {
      regType,
      firstName,
      lastName,
      gender: gender.value,
      citizenship: citizenship.value,
      birthDate: convertDateWithTimezoneOffsetToEpoch(birthDate),
      familyStatus: familyStatus.value,
      partnerFirstName,
      partnerLastName,
      dateOfAliyah: convertDateWithTimezoneOffsetToEpoch(dateOfAliyah),
      cameFrom: cameFrom.value,
      cameFromText
    }
  };

  const loadOptions = ddType => (inputValue, callback) => {
    loadOptionsByDDType({callback, language, ddType})
  };

  const isAgeUnder16 = date => {
    const birthDate = moment(date);
    const age = moment().diff(birthDate, 'years');

    return Boolean(date) && age < 16;
  };

  const isSubmitDisabled = values => isAgeUnder16(values.birthDate);

  const handleBirthDateChange = date => {
    isAgeUnder16(date) && showGlobalErrorMessage(E.STEP_2_UNDER_16);
  };

  const resetCameFromText = setFieldValue => selected => {
    if (selected?.value !== 6) {
      setFieldValue('cameFromText', '');
    }
  }

  const handleCheckIdNumber = (values) => {
    Promise.resolve()
      .then(() => {
        if (values.idNumber) {
          return checkIdNumber(values.idNumber);
        }
      })
      .then(setIdNumberUsed)
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validate}
      enableReinitialize={true}
      validateOnChange={true}
      validateOnBlur={false}
    >
      {({values, isValid, submitCount, setFieldValue}) => (
        <Step2View
          loadOptions={{
            gender: loadOptions(ddTypes.genderItems),
            citizenship: loadOptions(ddTypes.citizenshipItems),
            countries: loadOptions(ddTypes.countriesItems),
            kupatHolim: loadOptions(ddTypes.kupatHolimItems),
            familyStatus: loadOptions(ddTypes.familyStatusItems),
            cameFrom: loadOptions(ddTypes.cameFromItems),
          }}
          values={values}
          isValid={isValid || submitCount === 0}
          submitDisabled={isSubmitDisabled(values) || idNumberUsed}
          onBirthDateChange={handleBirthDateChange}
          resetCameFromText={resetCameFromText(setFieldValue)}
          regType={regType}
          handleCheckIdNumber={handleCheckIdNumber}
          idNumberUsed={idNumberUsed}
        />
      )}
    </Formik>
  )
};

Step2.propTypes = {
  ...types.router,
  regType: PropTypes.string,
  language: PropTypes.string,
  submitStep: PropTypes.func,
  uploadFile: PropTypes.func,
  payload: PropTypes.object,
  showGlobalErrorMessage: PropTypes.func,
};

export default Step2;
